import * as React from 'react';
import {variationName} from '@shopify/react-utilities/styles';
import classNames from 'classnames';
import {TextAlign} from '../Types';
import style from './text.scss';

export type TextType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'link'
  | 'small'
  | 'smaller'
  | 'badge'
  | 'subhead'
  | 'subhead2'
  | 'regular'
  | 'legal'
  | 'error';

type SpanElementProps = JSX.IntrinsicElements['span'];
type AnchorElementProps = JSX.IntrinsicElements['a'];
type ComposedProps = SpanElementProps & AnchorElementProps;

export interface TextProps extends ComposedProps {
  inheritColor?: boolean;
  inheritSize?: boolean;
  secondary?: boolean;
  emphasis?: boolean;
  type?: TextType;
  text?: React.ReactNode;
  align?: TextAlign;
  style?: any;
  ellipsis?: boolean;
  component?: string | React.ComponentClass;
  children?: React.ReactNode;
  className?: string;
  resetMargin?: boolean;
  hidden?: boolean;
}

const TYPE_TO_ELEMENT_MAP = {
  link: 'span',
  small: 'span',
  badge: 'span',
  legal: 'span',
  smaller: 'span',
  subhead: 'span',
  subhead2: 'span',
  h3: 'span',
  h4: 'span',
  h5: 'span',
};

function getElementType({component, type}: TextProps) {
  if (component) {
    return component;
  }
  if (!type) {
    return DEFAULT_ELEMENT;
  }
  if (TYPE_TO_ELEMENT_MAP[type]) {
    return TYPE_TO_ELEMENT_MAP[type];
  }
  return type;
}

const DEFAULT_ELEMENT = 'span';

export function Text(props: TextProps) {
  const {
    type = '',
    text = '',
    align = 'inherit',
    style: propStyle = {},
    ellipsis = false,
    children,
    component,
    secondary = false,
    emphasis = false,
    inheritSize = false,
    inheritColor = false,
    className = '',
    resetMargin = false,
    hidden = false,
    ...restProps
  } = props;

  const elementClassName = classNames(
    className,
    style.text,
    type && style[type],
    style[variationName('align', align)],
    {
      [style.ellipsis]: ellipsis,
      [style.emphasis]: emphasis,
      [style.secondary]: secondary,
      [style.resetMargin]: resetMargin,
      [style.inheritSize]: inheritSize,
      [style.inheritColor]: inheritColor,
      [style.hidden]: hidden,
    },
  );

  const elementProps = {
    style: propStyle,
    className: elementClassName,
    ...restProps,
  };

  return React.createElement(getElementType(props), elementProps, text || children);
}

export default Text;
